import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { LuZap, LuClock } from "react-icons/lu";
import { listJourneys, toggleJourney, updateJourneyStep } from "../../Services/phase2Service";

const Toggle = ({ on, onChange }) => (
  <button
    onClick={onChange}
    className={`w-11 h-6 rounded-full relative transition-colors ${on ? "bg-[#425F80]" : "bg-gray-300"}`}
  >
    <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
  </button>
);

const StepEditor = ({ step, onSaved }) => {
  const [form, setForm] = useState({ title: step.title, body: step.body || "", delay_hours: step.delay_hours });
  const [saving, setSaving] = useState(false);
  const dirty =
    form.title !== step.title || form.body !== (step.body || "") || Number(form.delay_hours) !== step.delay_hours;

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateJourneyStep(step.id, {
        title: form.title,
        body: form.body,
        delay_hours: Number(form.delay_hours),
      });
      toast.success("Step saved");
      onSaved?.(updated);
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-gray-100 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-2 text-xs text-gray-400">
        <LuClock size={13} />
        <input
          type="number"
          value={form.delay_hours}
          onChange={(e) => setForm({ ...form, delay_hours: e.target.value })}
          className="w-16 border border-gray-200 rounded px-2 py-0.5 text-gray-700"
        />
        hours after previous step
      </div>
      <input
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-medium mb-2"
      />
      <textarea
        value={form.body}
        onChange={(e) => setForm({ ...form, body: e.target.value })}
        className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm h-16"
      />
      {dirty && (
        <button
          onClick={save}
          disabled={saving}
          className="mt-2 bg-[#425F80] text-white rounded-lg px-4 py-1.5 text-xs disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      )}
    </div>
  );
};

const JourneysAdmin = () => {
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    listJourneys().then((d) => setJourneys(d || [])).catch(() => setJourneys([])).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);

  const flip = async (j) => {
    setJourneys((prev) => prev.map((x) => (x.id === j.id ? { ...x, is_active: !x.is_active } : x)));
    await toggleJourney(j.id, !j.is_active).catch(() => {});
  };

  return (
    <div className="p-6">
      <div className="flex items-center gap-2 mb-1">
        <LuZap className="text-[#425F80]" size={24} />
        <h1 className="text-xl font-bold text-gray-800">Drip Journeys</h1>
      </div>
      <p className="text-gray-500 mb-6">Automated message sequences sent to advisors. Edits apply to new sends.</p>

      {loading ? (
        <p className="text-gray-400">Loading…</p>
      ) : journeys.length === 0 ? (
        <p className="text-gray-400">No journeys (run migration 006).</p>
      ) : (
        <div className="space-y-6 max-w-3xl">
          {journeys.map((j) => (
            <div key={j.id} className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-1">
                <div>
                  <h3 className="font-semibold text-gray-800">{j.name}</h3>
                  <p className="text-xs text-gray-400">Trigger: {j.trigger_event}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${j.is_active ? "text-green-600" : "text-gray-400"}`}>
                    {j.is_active ? "Active" : "Off"}
                  </span>
                  <Toggle on={j.is_active} onChange={() => flip(j)} />
                </div>
              </div>
              <div className="space-y-3 mt-4">
                {(j.steps || []).map((s, i) => (
                  <div key={s.id} className="flex gap-3">
                    <span className="text-xs font-semibold text-gray-300 pt-3">{i + 1}</span>
                    <div className="flex-1">
                      <StepEditor step={s} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default JourneysAdmin;
